import React, { useCallback, useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';
import styles from '../Tabs.module.scss';
import ComposableTabsContext from './ComposableTabsContext';
import Title from '../../Typography/Title/Title';
import Gutter from '../../Gutter/Gutter';
import Element from '../../Element/Element';
import { ComposableTabsListContext } from './ComposableTabsList';

type ComposableTabsTabProps = {
  /** Text shown as tab content */
  children: string;
  /** Every tab in TabList should have unique tabKey
   * When contexts ComposableTabs activeTab matches tabKey, tab is shown as active */
  tabKey: string;
};

/** Component that renders single button should be rendered inside ComposableTabList component */
const ComposableTabsTab: React.FunctionComponent<ComposableTabsTabProps> = ({
  tabKey,
  children,
}) => {
  const { activeTab, onActiveTabChange, tabsId } = useContext(
    ComposableTabsContext
  );
  const tabListContext = useContext(ComposableTabsListContext);
  const ref = useRef<HTMLButtonElement>(null);
  const onClick = useCallback(() => {
    onActiveTabChange?.(tabKey);
  }, [onActiveTabChange, tabKey]);
  useEffect(() => {
    if (tabListContext.activatedTab === tabKey) {
      onClick();
      ref.current?.focus();
    }
  }, [tabListContext]);
  const active = activeTab === tabKey;

  return (
    <Element
      refElement={ref}
      id={`${tabsId}-tab-button-${tabKey}`}
      data-tab-for={tabsId}
      data-tab-key={tabKey}
      tagName="button"
      onClick={onClick}
      tabIndex={active ? 0 : -1}
      aria-selected={active}
      aria-controls={`${tabsId}-panel`}
      backgroundColor="transparent"
      className={classnames({
        [styles.tabs_tabHeading]: true,
        [styles.tabs_tabHeading__active]: active,
      })}
      margin={{ right: 'sm' }}
    >
      <Title color={active ? 'gray800' : 'gray500'} block uppercase>
        {children}
      </Title>

      <Gutter size="xs" />
    </Element>
  );
};

export default ComposableTabsTab;
